import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as ExportIcon } from '../../assets/icons/export.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/filter.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import { ReactComponent as KeyIcon } from '../../assets/icons/key.svg';
import { ReactComponent as UserEditIcon } from '../../assets/icons/user-edit.svg';
import { ReactComponent as CrossIcon } from '../../assets/icons/cross.svg';
import SearchBar from '../SearchBar/SearchBar';
import TableauDeBord from '../TableauDeBord/TableauDeBord';
import Button from '../Button/Button';
import CreationComptesModal from '../CreationComptesModal/CreationComptesModal';
import ResetPasswordModal from '../ResetPasswordModal/ResetPasswordModal';
import EditUserModal from '../EditUserModal/EditUserModal';
import {
  convertApiRoleToUiRole,
  convertUiRoleToApiRole,
  isClientRole,
  isPrestataireRole,
  isAdminRole
} from '../../utils/roleUtils';
import './GestionComptes.css';
import './menu-actions.css'; // Ajout du style spécifique pour les menus

interface Column {
  field: string;
  header: string;
  renderCell: (value: any, row: any) => React.ReactNode;
}

interface GestionComptesProps {
  userType?: 'acheteur' | 'prestataire';  // Type d'utilisateur pour filtrer les données
}

const GestionComptes: React.FC<GestionComptesProps> = ({ userType = 'acheteur' }) => {
  const { user } = useAuth();
  const [isChangeRoleModalOpen, setIsChangeRoleModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [validatorsCount, setValidatorsCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [tempSelectedRole, setTempSelectedRole] = useState<string | null>(null);
  const [userData, setUserData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Utiliser isClientRole pour déterminer le type d'utilisateur
  const userRole = user?.role || '';
  console.log('GestionComptes - Current user role:', userRole);

  // Vérifier si l'utilisateur est un client ou un prestataire
  const isClient = isClientRole(userRole);
  const isPrestataire = isPrestataireRole(userRole);
  const isAdmin = isAdminRole(userRole);

  console.log('Is client role:', isClient);
  console.log('Is prestataire role:', isPrestataire);
  console.log('Is admin role:', isAdmin);

  // Définition dynamique des tabs selon le rôle
  const tabs = isClient
    ? ['Tous', 'Acheteur signataire admin', 'Acheteur signataire', 'Acheteur']
    : ['Tous', 'Prestataire signataire admin', 'Prestataire signataire', 'Interlocuteur'];

  // Charger les données des utilisateurs depuis l'API
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error("Token d'authentification non trouvé");

        // Utiliser la bonne route en fonction du rôle de l'utilisateur (organismes pour acheteurs, entites pour prestataires)
        const apiRoute = isPrestataire ? `${process.env.REACT_APP_API_URL}/entites` : `${process.env.REACT_APP_API_URL}/organismes`;
        
        // Récupération des organismes ou entités avec leurs utilisateurs
        const response = await fetch(apiRoute, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur lors de la récupération des utilisateurs: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.status !== 'success' || !data.data) {
          throw new Error('Format de réponse API inattendu');
        }

        // Transformer les données des organismes/entités en une liste d'utilisateurs
        const users: any[] = [];

        if (isPrestataire) {
          // Traitement pour les prestataires (entités)
          data.data.forEach((entite: any) => {
            // Ajouter les gestionnaires (Prestataire signataire admin)
            if (entite.gestionnaires && entite.gestionnaires.length > 0) {
              entite.gestionnaires.forEach((gestionnaire: any) => {
                users.push({
                  id: gestionnaire.id,
                  idUser: gestionnaire.id_user,
                  nom: `${gestionnaire.prenom} ${gestionnaire.nom}`,
                  prenom: gestionnaire.prenom,
                  email: gestionnaire.email_contact,
                  role: 'Prestataire signataire admin',
                  fonction: gestionnaire.fonction || '',
                  telephone: gestionnaire.telephone || '',
                  organisme: entite.nom,
                  nombreCommande: 0,
                  valeurCommande: 0,
                  dateCreation: new Date(gestionnaire.created_at).toLocaleDateString('fr-FR')
                });
              });
            }

            // Ajouter les signataires (Prestataire signataire)
            if (entite.signataires && entite.signataires.length > 0) {
              entite.signataires.forEach((signataire: any) => {
                users.push({
                  id: signataire.id,
                  idUser: signataire.id_user,
                  nom: `${signataire.prenom} ${signataire.nom}`,
                  prenom: signataire.prenom,
                  email: signataire.email_contact,
                  role: 'Prestataire signataire',
                  fonction: signataire.fonction || '',
                  telephone: signataire.telephone || '',
                  organisme: entite.nom,
                  nombreCommande: 0,
                  valeurCommande: 0,
                  dateCreation: new Date(signataire.created_at).toLocaleDateString('fr-FR')
                });
              });
            }

            // Ajouter les interlocuteurs (Prestataire)
            if (entite.interlocuteurs && entite.interlocuteurs.length > 0) {
              entite.interlocuteurs.forEach((interlocuteur: any) => {
                users.push({
                  id: interlocuteur.id,
                  idUser: interlocuteur.id_user,
                  nom: `${interlocuteur.prenom} ${interlocuteur.nom}`,
                  prenom: interlocuteur.prenom,
                  email: interlocuteur.email_contact,
                  role: 'Prestataire',
                  fonction: interlocuteur.fonction || '',
                  telephone: interlocuteur.telephone || '',
                  organisme: entite.nom,
                  nombreCommande: 0,
                  valeurCommande: 0,
                  dateCreation: new Date(interlocuteur.created_at).toLocaleDateString('fr-FR')
                });
              });
            }
          });
        } else {
          // Traitement pour les acheteurs (organismes)
          data.data.forEach((organisme: any) => {
            // Ajouter les responsables achat (Acheteur signataire admin)
            if (organisme.responsable_achat) {
              users.push({
                id: organisme.responsable_achat.id,
                idUser: organisme.responsable_achat.id_user,
                nom: `${organisme.responsable_achat.prenom} ${organisme.responsable_achat.nom}`,
                prenom: organisme.responsable_achat.prenom,
                email: organisme.responsable_achat.email_contact,
                role: 'Acheteur signataire admin',
                fonction: organisme.responsable_achat.fonction || '',
                telephone: organisme.responsable_achat.telephone || '',
                organisme: organisme.nom,
                nombreCommande: 0,
                valeurCommande: 0,
                dateCreation: new Date(organisme.responsable_achat.created_at).toLocaleDateString('fr-FR')
              });
            }

            // Ajouter les validateurs achat (Acheteur signataire)
            if (organisme.validateurs_achat && organisme.validateurs_achat.length > 0) {
              organisme.validateurs_achat.forEach((validateur: any) => {
                users.push({
                  id: validateur.id,
                  idUser: validateur.id_user,
                  nom: `${validateur.prenom} ${validateur.nom}`,
                  prenom: validateur.prenom,
                  email: validateur.email_contact,
                  role: 'Acheteur signataire',
                  fonction: validateur.fonction || '',
                  telephone: validateur.telephone || '',
                  organisme: organisme.nom,
                  nombreCommande: 0,
                  valeurCommande: 0,
                  dateCreation: new Date(validateur.created_at).toLocaleDateString('fr-FR')
                });
              });
            }

            // Ajouter les acheteurs (Acheteur)
            if (organisme.acheteurs && organisme.acheteurs.length > 0) {
              organisme.acheteurs.forEach((acheteur: any) => {
                users.push({
                  id: acheteur.id,
                  idUser: acheteur.id_user,
                  nom: `${acheteur.prenom} ${acheteur.nom}`,
                  prenom: acheteur.prenom,
                  email: acheteur.email_contact,
                  role: 'Acheteur',
                  fonction: acheteur.fonction || '',
                  telephone: acheteur.telephone || '',
                  organisme: organisme.nom,
                  nombreCommande: 0,
                  valeurCommande: 0,
                  dateCreation: new Date(acheteur.created_at).toLocaleDateString('fr-FR')
                });
              });
            }
          });
        }

        setUserData(users);
        setFilteredData(users);

        // Compter les validateurs
        const validatorsCount = users.filter(user => user.role === 'Acheteur signataire').length;
        setValidatorsCount(validatorsCount);

      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        setError(error instanceof Error ? error.message : 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isPrestataire]);

  // Fonction pour ouvrir le modal de changement de rôle
  const handleRoleClick = (userRow) => {
    // Vérifier si l'utilisateur connecté a le droit de modifier les rôles
    // et si le rôle de l'utilisateur à modifier n'est pas un rôle admin
    if (!isAdmin ||
        userRow.role === 'Acheteur signataire admin' ||
        userRow.role === 'Prestataire signataire admin' ||
        userRow.role.toLowerCase().includes('admin')) {
      return;
    }

    setSelectedUser(userRow);
    setIsChangeRoleModalOpen(true);
  };

  // Fonction pour gérer le changement de rôle
  const handleChangeRole = async (newRole: string) => {
    if (!selectedUser) return;

    try {
      // Vérification du nombre de validateurs si on veut passer à validateur
      if (newRole === 'Acheteur signataire') {
        // Compter les validateurs actuels (hors l'utilisateur sélectionné)
        const currentValidators = userData.filter(
          u => u.role === 'Acheteur signataire' && u.id !== selectedUser.id
        ).length;

        // Si on a déjà 2 validateurs, empêcher l'ajout d'un autre
        if (currentValidators >= 2) {
          alert('Vous ne pouvez pas avoir plus de 2 validateurs par organisme.');
          return;
        }
      }

      console.log(`Changing role for user ${selectedUser.id} to ${newRole}`);

      // Obtenir le rôle API correspondant
      const roleForApi = convertUiRoleToApiRole(newRole);

      // Déterminer la route API appropriée selon le rôle actuel et le nouveau rôle
      let apiRoute = '';
      const token = localStorage.getItem('token');

      // L'implémentation exacte dépendra de l'API disponible pour changer le rôle
      // Voici une approche possible basée sur les routes existantes
      await fetch(`${process.env.REACT_APP_API_URL}/users/${selectedUser.idUser}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify({ role: roleForApi })
      });

      // Mettre à jour les données localement
      const updatedData = userData.map(user => {
        if (user.id === selectedUser.id) {
          return { ...user, role: newRole };
        }
        return user;
      });

      setUserData(updatedData);
      setFilteredData(updatedData);

      // Fermer le modal après changement
      setIsChangeRoleModalOpen(false);
      setTempSelectedRole(null);

      // Recharger les données pour s'assurer qu'elles sont à jour
      window.location.reload();

    } catch (error) {
      console.error('Error updating role:', error);
      alert("Une erreur est survenue lors du changement de rôle.");
    }
  };

  const handleSearch = (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredData(userData);
      return;
    }

    const searchTermLower = searchTerm.toLowerCase();
    const filtered = userData.filter(item =>
      item.nom.toLowerCase().includes(searchTermLower) ||
      item.email.toLowerCase().includes(searchTermLower) ||
      item.organisme.toLowerCase().includes(searchTermLower)
    );

    setFilteredData(filtered);
  };

  const handleExport = () => {
    // Implémentation de l'export (CSV, Excel, etc.)
    console.log('Exporting user data...');

    // Exemple: Création d'un CSV basique
    const headers = ["Nom", "Email", "Rôle", "Fonction", "Organisme", "Date de création"];
    const csvData = [
      headers.join(","),
      ...filteredData.map(user => [
        `"${user.nom}"`,
        `"${user.email}"`,
        `"${user.role}"`,
        `"${user.fonction || ''}"`,
        `"${user.organisme || ''}"`,
        `"${user.dateCreation || ''}"`,
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'utilisateurs.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFilter = () => {
    console.log('Filtering...');
  };

  const handleCreateCompte = (formData) => {
    // Appel API pour créer le compte
    console.log('Création de compte:', formData);
  };

  const handleEditUser = (userId: string) => {
    console.log(`Modifier les informations de l'utilisateur ${userId}`);
    const user = userData.find(u => u.id === userId);
    setSelectedUser(user);
    setIsEditUserModalOpen(true);
  };

  const handleSaveUser = async (userId: string, updatedUserData: { nom: string; prenom: string; email: string; fonction: string }) => {
    try {
      if (!selectedUser) return false;

      const token = localStorage.getItem('token');

      let apiRoute;
      switch (selectedUser.role) {
        case 'Acheteur signataire admin':
          apiRoute = `${process.env.REACT_APP_API_URL}/responsables-achat/${selectedUser.id}`;
          break;
        case 'Acheteur signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/validateurs-achat/${selectedUser.id}`;
          break;
        case 'Acheteur':
          apiRoute = `${process.env.REACT_APP_API_URL}/acheteurs/${selectedUser.id}`;
          break;
        case 'Prestataire signataire admin':
          apiRoute = `${process.env.REACT_APP_API_URL}/gestionnaires/${selectedUser.id}`;
          break;
        case 'Prestataire signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/signataires/${selectedUser.id}`;
          break;
        case 'Interlocuteur':
          apiRoute = `${process.env.REACT_APP_API_URL}/interlocuteurs/${selectedUser.id}`;
          break;
        default:
          throw new Error(`Rôle non pris en charge: ${selectedUser.role}`);
      }

      // Structurer les données selon l'API
      const requestData = {
        nom: updatedUserData.nom,
        prenom: updatedUserData.prenom,
        email_contact: updatedUserData.email,
        fonction: updatedUserData.fonction
      };

      const response = await fetch(apiRoute, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }

      // Mise à jour locale des données
      const updatedData = userData.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            nom: updatedUserData.nom,
            email: updatedUserData.email,
            fonction: updatedUserData.fonction
          };
        }
        return user;
      });

      setUserData(updatedData);
      setFilteredData(updatedData);

      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      throw error;
    }
  };

  const handleResetPassword = (userId: string) => {
    console.log(`Réinitialiser le mot de passe pour l'utilisateur ${userId}`);
    setSelectedUser(userData.find(user => user.id === userId));
    setIsResetPasswordModalOpen(true);
  };

  // Fonction pour supprimer un utilisateur
  const handleDeleteUser = (userId: string) => {
    // Confirmer la suppression
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      console.log(`Supprimer l'utilisateur ${userId}`);
      // Implémentation de la suppression via API
      const token = localStorage.getItem('token');
      // Trouver l'utilisateur pour déterminer son rôle et la bonne route API
      const userToDelete = userData.find(user => user.id === userId);
      if (!userToDelete) return;

      // Déterminer la bonne route API en fonction du rôle de l'utilisateur à supprimer
      let apiRoute = '';
      switch (userToDelete.role) {
        case 'Prestataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/interlocuteurs/${userId}`;
          break;
        case 'Prestataire signataire':
          apiRoute = `${process.env.REACT_APP_API_URL}/signataires/${userId}`;
          break;
        default:
          alert("Type d'utilisateur non pris en charge pour la suppression");
          return;
      }

      // Appel API pour supprimer l'utilisateur
      fetch(apiRoute, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        if (!response.ok) throw new Error('Erreur lors de la suppression');
        // Mettre à jour la liste d'utilisateurs localement
        const updatedData = userData.filter(user => user.id !== userId);
        setUserData(updatedData);
        setFilteredData(updatedData);
        alert("Utilisateur supprimé avec succès");
      })
      .catch(error => {
        console.error('Erreur:', error);
        alert("Erreur lors de la suppression de l'utilisateur");
      });
    }
  };

  // Fonction qui renvoie l'icône appropriée pour les actions de suppression
  const getDeleteIcon = () => {
    return <CrossIcon />;
  };

  // Définir les options d'action pour chaque ligne en fonction du rôle de l'utilisateur
  const getActionOptions = (row: any) => {
    const userId = row.id;
    const rowRole = row.role;
    
    // Si l'utilisateur connecté est un prestataire admin (Gestionnaire)
    if (userRole === 'Prestataire signataire admin') {
      // Les options disponibles dépendent du rôle de l'utilisateur dans la ligne
      if (rowRole === 'Prestataire signataire admin') {
        // Un gestionnaire ne peut pas modifier un autre gestionnaire
        return [];
      } else if (rowRole === 'Prestataire signataire') {
        // Pour un signataire, on peut modifier, réinitialiser et supprimer
        return [
          {
            label: 'Modifier les informations',
            onClick: () => handleEditUser(userId),
            icon: <UserEditIcon />
          },
          {
            label: 'Réinitialiser le mot de passe',
            onClick: () => handleResetPassword(userId),
            icon: <KeyIcon />
          },
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      } else if (rowRole === 'Prestataire') {
        // Pour un interlocuteur, on peut modifier, promouvoir, réinitialiser et supprimer
        return [
          {
            label: 'Modifier les informations',
            onClick: () => handleEditUser(userId),
            icon: <UserEditIcon />
          },
          {
            label: 'Promouvoir',
            onClick: () => handleRoleClick(row),
            icon: <EditIcon />
          },
          {
            label: 'Réinitialiser le mot de passe',
            onClick: () => handleResetPassword(userId),
            icon: <KeyIcon />
          },
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      }
    } 
    // Si l'utilisateur connecté est un prestataire signataire
    else if (userRole === 'Prestataire signataire') {
      // Un signataire peut uniquement supprimer des interlocuteurs
      if (rowRole === 'Prestataire') {
        return [
          {
            label: 'Supprimer',
            onClick: () => handleDeleteUser(userId),
            icon: <CrossIcon />
          }
        ];
      }
      return [];
    }
    
    // Pour les autres rôles (acheteurs), comportement par défaut
    return [
      {
        label: 'Modifier les informations',
        onClick: () => handleEditUser(userId),
        icon: <UserEditIcon />
      },
      {
        label: 'Réinitialiser le mot de passe',
        onClick: () => handleResetPassword(userId),
        icon: <KeyIcon />
      }
    ];
  };

  const columns = [
    {
      field: 'nom',
      header: 'Nom / Prénom',
      renderCell: (value: string) => (
        <div className="user-name">
          {value}
        </div>
      )
    },
    {
      field: 'email',
      header: 'Email',
      renderCell: (value: string) => (
        <div className="user-email">
          {value}
        </div>
      )
    },
    // Si c'est un prestataire, afficher téléphone au lieu de nombre de commande
    {
      field: isPrestataire ? 'telephone' : 'nombreCommande',
      header: isPrestataire ? 'Téléphone' : 'Nombre de commande',
      renderCell: (value: any) => (
        <div className={isPrestataire ? "user-phone" : "commande-count"}>
          {isPrestataire ? (value || '-') : value}
        </div>
      )
    },
    // Pour les prestataires, afficher la fonction au lieu des valeurs de commande
    {
      field: isPrestataire ? 'fonction' : 'valeurCommande',
      header: isPrestataire ? 'Fonction' : 'Valeurs de commande',
      renderCell: (value: any) => (
        <div className={isPrestataire ? "user-fonction" : "commande-value"}>
          {isPrestataire ? (value || '-') : `${value.toLocaleString('fr-FR')} €`}
        </div>
      )
    },
    {
      field: 'role',
      header: 'Rôle',
      renderCell: (value: string, row: any) => {
        if (!row) {
          return <div className="user-role">{value}</div>;
        }

        // Utiliser isAdminRole pour déterminer si l'utilisateur peut modifier les rôles
        // On vérifie aussi que le rôle de l'utilisateur à modifier n'est pas un rôle admin
        const isRoleEditable =
          isAdminRole(userRole) && // L'utilisateur connecté est un admin
          !row.role.toLowerCase().includes('admin'); // L'utilisateur à modifier n'est pas un admin

        return (
          <div
            className="user-role"
            onClick={isRoleEditable ? () => handleRoleClick(row) : undefined}
            style={{
              cursor: isRoleEditable ? 'pointer' : 'default',
              textDecoration: isRoleEditable ? 'underline' : 'none',
              opacity: isRoleEditable ? 1 : 0.8
            }}
          >
            <span>{value}</span>
            {isRoleEditable && <EditIcon className="edit-icon" />}
          </div>
        );
      }
    },
    {
      field: 'dateCreation',
      header: 'Date de création',
      renderCell: (value: string) => (
        <div className="creation-date">
          {value}
        </div>
      )
    }
  ];

  // Définir les options d'action spécifiques au changement de rôle
  const getRoleActionOptions = (userId: string) => {
    const userToUpdate = mockData.find(u => u.id === userId);

    // Si l'utilisateur est Admin ou si l'utilisateur connecté n'est pas responsable_achat,
    // ne pas permettre de changer le rôle
    if (userToUpdate?.role === 'Admin' || user?.role !== 'responsable_achat') {
      return [];
    }

    return [
      {
        label: 'Définir comme Acheteur',
        onClick: () => handleChangeRole(userId, 'Acheteur'),
        icon: <UserEditIcon />
      },
      {
        label: 'Définir comme Validateur',
        onClick: () => handleChangeRole(userId, 'Validateur'),
        icon: <UserEditIcon />
      }
    ];
  };

  if (loading) {
    return <div className="loading-indicator">Chargement des utilisateurs...</div>;
  }

  if (error) {
    return <div className="error-message">Erreur: {error}</div>;
  }

  return (
    <div className="gestion-comptes">
      <div className="gestion-comptes-header">
        {/* N'afficher le bouton de création que pour les administrateurs */}
        {isAdminRole(userRole) && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button
              value="Créer un utilisateur"
              variant="primary"
              size="small"
              icon={<PlusIcon />}
              onClick={() => setIsCreateModalOpen(true)}
            />
          </div>
        )}
      </div>
      <div className="tableau-section">
        <TableauDeBord
          data={filteredData}
          columns={columns}
          searchPlaceholder="Trouver un utilisateur"
          onSearch={handleSearch}
          onExport={handleExport}
          onFilter={handleFilter}
          isSelectable={false}
          getActionOptions={getActionOptions}
          selectedItems={[]}
          onSelectionChange={() => {}}
        />
      </div>

      {/* Modals */}
      {isAdminRole(userRole) && (
        <>
          {/* Modal de création de compte */}
          <CreationComptesModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onCreate={handleCreateCompte}
          />

          {/* Modal de réinitialisation de mot de passe */}
          <ResetPasswordModal
            isOpen={isResetPasswordModalOpen}
            onClose={() => setIsResetPasswordModalOpen(false)}
            onSendLink={(email) => { console.log(`Send reset link to ${email}`); }}
            onDirectReset={(userId, password) => { console.log(`Reset password for ${userId} to ${password}`); }}
            userId={selectedUser?.id}
            userEmail={selectedUser?.email}
          />

          {/* Modal d'édition utilisateur */}
          {selectedUser && (
            <EditUserModal
              isOpen={isEditUserModalOpen}
              onClose={() => setIsEditUserModalOpen(false)}
              onSave={handleSaveUser}
              userId={selectedUser.id}
              userName={selectedUser.nom}
              userEmail={selectedUser.email}
              userPrenom={selectedUser.prenom}
              userFonction={selectedUser.fonction}
              userRole={selectedUser.role}
            />
          )}

          {/* Modal de changement de rôle */}
          {selectedUser && (
            <div className={`modal-overlay ${isChangeRoleModalOpen ? 'show' : 'hide'}`}
                 style={{ display: isChangeRoleModalOpen ? 'flex' : 'none' }}>
              <div className="change-role-modal">
                <h2>Changer le rôle de {selectedUser.nom}</h2>
                <p>Rôle actuel: {selectedUser.role}</p>

                {/* État temporaire pour stocker le nouveau rôle sélectionné */}
                <div className="role-options">
                  {/* Afficher les options de rôle appropriées en fonction du type d'utilisateur */}
                  {isPrestataireRole(selectedUser.role) ? (
                    // Options pour les prestataires
                    <>
                      <button
                        className={`role-button ${
                          selectedUser.role === 'Prestataire'
                            ? 'active'
                            : tempSelectedRole === 'Prestataire'
                              ? 'selected'
                              : ''
                        }`}
                        onClick={() => setTempSelectedRole('Prestataire')}
                        disabled={selectedUser.role === 'Prestataire'}
                      >
                        Prestataire
                      </button>
                      <button
                        className={`role-button ${
                          selectedUser.role === 'Prestataire signataire'
                            ? 'active'
                            : tempSelectedRole === 'Prestataire signataire'
                              ? 'selected'
                              : ''
                        }`}
                        onClick={() => setTempSelectedRole('Prestataire signataire')}
                        disabled={selectedUser.role === 'Prestataire signataire'}
                      >
                        Prestataire signataire
                      </button>
                    </>
                  ) : (
                    // Options pour les acheteurs
                    <>
                      <button
                        className={`role-button ${
                          selectedUser.role === 'Acheteur'
                            ? 'active'
                            : tempSelectedRole === 'Acheteur'
                              ? 'selected'
                              : ''
                        }`}
                        onClick={() => setTempSelectedRole('Acheteur')}
                        disabled={selectedUser.role === 'Acheteur'}
                      >
                        Acheteur
                      </button>
                      <button
                        className={`role-button ${
                          selectedUser.role === 'Acheteur signataire'
                            ? 'active'
                            : tempSelectedRole === 'Acheteur signataire'
                              ? 'selected'
                              : ''
                        }`}
                        onClick={() => setTempSelectedRole('Acheteur signataire')}
                        disabled={selectedUser.role === 'Acheteur signataire'}
                      >
                        Acheteur signataire
                      </button>
                    </>
                  )}
                </div>

                <div className="modal-actions">
                  <Button
                    variant="secondary"
                    size="small"
                    value="Annuler"
                    onClick={() => {
                      setIsChangeRoleModalOpen(false);
                      setTempSelectedRole(null);
                    }}
                  />
                  <Button
                    variant="primary"
                    size="small"
                    value="Valider"
                    disabled={!tempSelectedRole || tempSelectedRole === selectedUser.role}
                    onClick={() => {
                      if (tempSelectedRole) {
                        handleChangeRole(tempSelectedRole);
                        setTempSelectedRole(null);
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GestionComptes;